import React, { useState } from "react";
import { Button } from "~/components/Button";
import { Input } from "~/components/Controls/Input";
import { ISelectItem } from "~/components/Controls/Select/common";
import { DropDown } from "~/components/DropDown";
import formStyle from "~/components/Form/index.module.scss";
import { IModalComponentClose, useModals } from "~/components/Modal";
import { cx } from "~/lib/cx";
import { Intent } from "~/lib/intent";
import selectStyle from "./index.module.scss";
import style from "./MultiSelectPopup.module.scss";

export interface ISelectItemGroup {
  label: string;
  items: ISelectItem<string>[];
}

interface IMultiSelectPopupContentProps {
  groups: ISelectItemGroup[];
  value: string[];
  title?: string;
  onApply: (value: string[]) => void;
  close?: IModalComponentClose;
}

const groupedByQuery = (groups: ISelectItemGroup[], predicate: (item: ISelectItem<string>) => boolean, query: string) => {
  const q = query.trim().toLowerCase();
  return groups
    .map((group) => ({
      label: group.label,
      items: group.items.filter((item) => predicate(item) && (!q || item.label.toLowerCase().includes(q))),
    }))
    .filter((group) => group.items.length > 0);
};

const MultiSelectPopupContent = ({ groups, value, title, onApply, close }: IMultiSelectPopupContentProps) => {
  const [draft, setDraft] = useState<string[]>(value);
  const [query, setQuery] = useState("");

  const add = (item: string) => setDraft((current) => current.concat(item));
  const remove = (item: string) => setDraft((current) => current.filter((v) => v !== item));

  const availableGroups = groupedByQuery(groups, (item) => !draft.includes(item.value), query);
  const selectedGroups = groupedByQuery(groups, (item) => draft.includes(item.value), query);

  return (
    <div className={formStyle.form}>
      <div className={formStyle.formTitle}>{title ?? "Select"}</div>
      <Input value={query} onChange={setQuery} placeholder="Search" rightIcon={<i className="fa fa-search" />} />
      <div className={style.columns}>
        <div className={style.column}>
          <div className={style.columnHeader}>Available</div>
          <div className={cx(selectStyle.selectItems, style.columnList)}>
            {availableGroups.length === 0 && <div className={selectStyle.selectEmpty}>Nothing found</div>}
            {availableGroups.map((group) => (
              <div key={group.label} className={selectStyle.selectGroup}>
                {group.label && <div className={style.groupLabel}>{group.label}</div>}
                {group.items.map((item) => (
                  <div key={item.value} className={selectStyle.selectItem} onClick={() => add(item.value)}>
                    {item.label}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className={style.column}>
          <div className={style.columnHeader}>Selected ({draft.length})</div>
          <div className={cx(selectStyle.selectItems, style.columnList)}>
            {selectedGroups.length === 0 && <div className={selectStyle.selectEmpty}>Nothing selected yet</div>}
            {selectedGroups.map((group) => (
              <div key={group.label} className={selectStyle.selectGroup}>
                {group.label && <div className={style.groupLabel}>{group.label}</div>}
                {group.items.map((item) => (
                  <div key={item.value} className={selectStyle.selectItem} onClick={() => remove(item.value)}>
                    {item.label}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={formStyle.formControls}>
        <Button onClick={() => close?.()}>Cancel</Button>
        <Button
          intent={Intent.PRIMARY}
          onClick={() => {
            onApply(draft);
            close?.();
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

export interface IMultiSelectPopupProps {
  groups: ISelectItemGroup[];
  value: string[] | null;
  onChange: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  title?: string;
}

export const MultiSelectPopup = ({ groups, value, onChange, disabled, placeholder = "Select…", title }: IMultiSelectPopupProps) => {
  const modals = useModals();
  const popup = modals.create(MultiSelectPopupContent);

  const safeValue = value ?? [];
  const selectedCount = groups.flatMap((group) => group.items).filter((item) => safeValue.includes(item.value)).length;

  return (
    <DropDown
      disabled={disabled}
      isOpen={false}
      onToggle={(open) => open && popup.show({ groups, value: safeValue, title, onApply: onChange })}
      content={() => null}
    >
      <div className={selectStyle.selectCurrentItem}>
        {selectedCount > 0 ? (
          <span>{selectedCount} selected</span>
        ) : (
          <span className={selectStyle.selectPlaceholder}>{placeholder}</span>
        )}
      </div>
    </DropDown>
  );
};
